import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Computer,
  Timer,
  TrendingUp,
  AccessTime,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import moment from 'moment-timezone';

interface ActivityProductivityCardProps {
  compact?: boolean;
}

const ActivityProductivityCard: React.FC<ActivityProductivityCardProps> = ({ compact = false }) => {
  const { trackingEnabled } = useSettings();
  const { user } = useAuth();
  const [isAutoRefreshPaused, setIsAutoRefreshPaused] = useState<boolean>(false);

  // Check if user has a device assigned (using employee-facing endpoint)
  const { data: devicesData } = useQuery({
    queryKey: ['myDevices'],
    queryFn: () => apiClient.devices.getMyDevice(),
    enabled: !!user?.id && trackingEnabled,
  });

  const hasDevice = devicesData?.data?.data && devicesData.data.data.length > 0;

  // Get activity data
  const { data: activityData, isLoading } = useQuery({
    queryKey: ['myActivity'],
    queryFn: () => apiClient.activityTracking.getMyActivity(),
    enabled: trackingEnabled && hasDevice,
    refetchInterval: isAutoRefreshPaused ? false : 60000, // Refresh every minute when not paused
  });

  // Show alert banner if tracking is disabled
  if (!trackingEnabled) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Tracking Disabled:</strong> Activity tracking is currently disabled. Contact your administrator to enable tracking.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Show message if no device assigned
  if (!hasDevice) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Computer color="primary" />
            <Typography variant="h6">Activity & Productivity</Typography>
          </Box>
          <Alert severity="info">
            No device assigned. Activity tracking will begin once a device is registered.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const activity = activityData?.data?.data;
  const today = activity?.today || {
    activeMinutes: 0,
    idleMinutes: 0,
    totalMinutes: 0,
    activePercentage: 0,
  };

  if (compact) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Computer color="primary" />
            <Typography variant="h6">Activity Today</Typography>
          </Box>
          {isLoading ? (
            <LinearProgress />
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Active: {today.activeMinutes} min
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Idle: {today.idleMinutes} min
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={today.activePercentage}
                sx={{ height: 8, borderRadius: 4 }}
                color={today.activePercentage >= 80 ? 'success' : today.activePercentage >= 60 ? 'warning' : 'error'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {today.activePercentage}% active
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Computer color="primary" />
            <Typography variant="h6">Activity & Productivity</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={isAutoRefreshPaused ? "Paused" : "Auto-Refresh"}
                size="small"
                color={isAutoRefreshPaused ? "warning" : "success"}
                variant="outlined"
              />
              <Tooltip title={isAutoRefreshPaused ? "Resume auto-refresh" : "Pause auto-refresh"}>
                <IconButton
                  size="small"
                  onClick={() => setIsAutoRefreshPaused(!isAutoRefreshPaused)}
                  color={isAutoRefreshPaused ? "success" : "warning"}
                >
                  {isAutoRefreshPaused ? <PlayArrow /> : <Pause />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

        {isLoading ? (
          <LinearProgress />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                <Timer color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="primary">
                  {today.activeMinutes}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Minutes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                <AccessTime color="warning" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {today.idleMinutes}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Idle Minutes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Productivity Score
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {today.activePercentage}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={today.activePercentage}
                  sx={{ height: 10, borderRadius: 5 }}
                  color={today.activePercentage >= 80 ? 'success' : today.activePercentage >= 60 ? 'warning' : 'error'}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total: {today.totalMinutes} minutes tracked
                  </Typography>
                  <Chip
                    icon={<TrendingUp />}
                    label={today.activePercentage >= 80 ? 'Excellent' : today.activePercentage >= 60 ? 'Good' : 'Needs Improvement'}
                    size="small"
                    color={today.activePercentage >= 80 ? 'success' : today.activePercentage >= 60 ? 'warning' : 'error'}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityProductivityCard;

